import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BrandingProvider } from './contexts/TenantBrandingContext';
import { FeatureFlagProvider } from './contexts/FeatureFlagContext';
import { MainLayout } from './components/Layout/MainLayout';
import { LoginPage } from './pages/Auth/LoginPage';
import { RegisterPage } from './pages/Auth/RegisterPage';
import { EmailVerificationPage } from './pages/Auth/EmailVerificationPage';
import { ForgotPasswordPage } from './pages/Auth/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/Auth/ResetPasswordPage';
import { InviteAcceptancePage } from './pages/Auth/InviteAcceptancePage';
import { DashboardPage } from './pages/DashboardPage';
import { ServerSettingsPage } from './pages/ServerAdmin/ServerSettingsPage';
import { ServerAdminPage } from './pages/ServerAdmin/ServerAdminPage';
import { ProtectedRoute } from './components/ProtectedRoute/ProtectedRoute';
import { ServerAdminLayout } from './components/Layout/ServerAdminLayout';

// Import existing components
import AnalyticsDashboard from './components/AnalyticsDashboard';
import BillingPage from './pages/Billing/BillingPage';
import { CheckoutSuccessPage } from './pages/Billing/CheckoutSuccessPage';
import BrandingWizard from './components/BrandingWizard';
import { CampaignBuilder } from './components/CampaignBuilder';
import { CampaignsListPage } from './pages/Campaigns/CampaignsListPage';
import { CampaignDetailPage } from './pages/Campaigns/CampaignDetailPage';
import ContactsPage from './components/ContactsPage';
import FeedbackInbox from './components/FeedbackInbox';
import { RatingGatePage } from './components/RatingGatePage';
import { RatingSettings } from './components/RatingSettings';
import SettingsPage from './components/SettingsPage';
import { TemplateEditor } from './components/TemplateEditor';
import { TemplatesListPage } from './pages/Templates/TemplatesListPage';
import { TemplateDetailPage } from './pages/Templates/TemplateDetailPage';
import { SequencesListPage } from './pages/Sequences/SequencesListPage';
import { SequenceDetailPage } from './pages/Sequences/SequenceDetailPage';
import { SequenceBuilder } from './components/SequenceBuilder';
import { OnboardingWizard } from './components/OnboardingWizard';
import { TeamManagementPage } from './pages/TeamManagementPage';
import UserGuidePage from './pages/UserGuidePage';


// App routes component
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/invite/:inviteToken" element={<InviteAcceptancePage />} />
      <Route path="/rating-gate" element={<RatingGatePage />} />
      
      {/* Protected routes */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns"
        element={
          <ProtectedRoute>
            <CampaignsListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/new"
        element={
          <ProtectedRoute>
            <CampaignBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id"
        element={
          <ProtectedRoute>
            <CampaignDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/campaigns/:id/edit"
        element={
          <ProtectedRoute>
            <CampaignBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/feedback"
        element={
          <ProtectedRoute>
            <FeedbackInbox />
          </ProtectedRoute>
        }
      />
      <Route
        path="/user-guide"
        element={
          <ProtectedRoute>
            <UserGuidePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <AnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates"
        element={
          <ProtectedRoute>
            <TemplatesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/new"
        element={
          <ProtectedRoute>
            <TemplateEditor />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/:id"
        element={
          <ProtectedRoute>
            <TemplateDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/templates/:id/edit"
        element={
          <ProtectedRoute>
            <TemplateEditor />
          </ProtectedRoute>
        }
      />
      {/* Sequences routes */}
      <Route
        path="/sequences"
        element={
          <ProtectedRoute>
            <SequencesListPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sequences/new"
        element={
          <ProtectedRoute>
            <SequenceBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sequences/:id"
        element={
          <ProtectedRoute>
            <SequenceDetailPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/sequences/:id/edit"
        element={
          <ProtectedRoute>
            <SequenceBuilder />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rating"
        element={
          <ProtectedRoute>
            <RatingSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <ProtectedRoute>
            <BillingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/billing/checkout-success"
        element={
          <ProtectedRoute>
            <CheckoutSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/branding"
        element={
          <ProtectedRoute>
            <BrandingWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/contacts"
        element={
          <ProtectedRoute>
            <ContactsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute>
            <TeamManagementPage />
          </ProtectedRoute>
        }
      />
      
      {/* Server Admin Routes (Superuser Only) */}
      <Route
        path="/server/admin"
        element={
          <ProtectedRoute requireSuperuser>
            <ServerAdminPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/server/settings"
        element={
          <ProtectedRoute requireSuperuser>
            <ServerSettingsPage />
          </ProtectedRoute>
        }
      />
      
      {/* Redirect any unmatched routes to dashboard */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Main App component
function App() {
  return (
    <AuthProvider>
      <FeatureFlagProvider>
        <BrandingProvider>
          <AppRoutes />
        </BrandingProvider>
      </FeatureFlagProvider>
    </AuthProvider>
  );
}

export default App;
